import { Recipe } from '../../api/Recipe';
import { apiModules, supabase } from '../../api/apiService';
import { supabaseService } from '../../services/SupabaseClient';

interface SubscribeToFavoriteRecipesOptions {
  favoritesRef: React.RefObject<Recipe[]>;
  setCachedFavorites: (favorites: Recipe[]) => void;
}

const getRecipeIdFromPayload = (payload: { eventType: string; new: unknown; old: unknown }): string =>
  payload.eventType === 'DELETE'
    ? String((payload.old as { id?: string } | null)?.id || '')
    : String((payload.new as { id?: string } | null)?.id || '');

const includesFavoriteRecipe = (
  favorites: Recipe[] | null | undefined,
  recipeId: string
): boolean => !!recipeId && !!favorites?.some((favorite) => favorite.id === recipeId);

const replaceFavoriteRecipe = (favorites: Recipe[], updatedRecipe: Recipe): Recipe[] =>
  favorites.map((favorite) => (favorite.id === updatedRecipe.id ? updatedRecipe : favorite));

const removeFavoriteRecipe = (favorites: Recipe[], recipeId: string): Recipe[] =>
  favorites.filter((favorite) => favorite.id !== recipeId);

const createChannelCleanup = <TChannel>(
  removeChannel: (channel: TChannel) => Promise<unknown> | void,
  channel: TChannel
): (() => void) => () => {
  void removeChannel(channel);
};

export const subscribeToFavoriteRecipes = ({
  favoritesRef,
  setCachedFavorites,
}: SubscribeToFavoriteRecipesOptions): (() => void) => {
  const channel = supabase
    .channel('favorites_recipes_channel')
    .on<Recipe>(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'recipes' },
      async (payload) => {
        const targetRecipeId = getRecipeIdFromPayload(payload);

        if (!includesFavoriteRecipe(favoritesRef.current, targetRecipeId)) {
          return;
        }

        if (payload.eventType === 'DELETE') {
          setCachedFavorites(removeFavoriteRecipe(favoritesRef.current, targetRecipeId));
          return;
        }

        try {
          const updatedRecipe = await apiModules.recipes.getRecipeById(targetRecipeId);
          setCachedFavorites(replaceFavoriteRecipe(favoritesRef.current, updatedRecipe));
        } catch (error) {
          console.error('Error fetching complete updated recipe for Realtime UPDATE:', error);
        }
      }
    )
    .subscribe();

  return createChannelCleanup(supabase.removeChannel.bind(supabase), channel);
};

interface SubscribeToFavoriteRowsOptions {
  authorId: number;
  refreshFavorites: () => Promise<void>;
}

export const subscribeToFavoriteRows = async ({
  authorId,
  refreshFavorites,
}: SubscribeToFavoriteRowsOptions): Promise<() => void> => {
  const realtimeClient = await supabaseService.getClient();
  const channel = realtimeClient
    .channel(`favorites_rows_${authorId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'recipe_favorites',
        filter: `author_id=eq.${authorId}`,
      },
      () => {
        void refreshFavorites();
      }
    )
    .subscribe();

  return createChannelCleanup(realtimeClient.removeChannel.bind(realtimeClient), channel);
};
